import { Feature } from 'src/.generated/Feature.EnterpriseWeb.model';
import React from 'react';
import { Field, FormikValues, useFormikContext } from 'formik';
import { Text } from '@sitecore-jss/sitecore-jss-nextjs';
import Tooltip from 'src/helpers/Tooltip/Tooltip';
import { useTheme } from 'lib/context/ThemeContext';
import classNames from 'classnames';
import ImagePrimary from 'src/helpers/Media/ImagePrimary';
import { FormFieldsTheme } from '../../FormFields.Theme';
import { FormFieldProps } from 'lib/forms';
import { Button } from 'src/helpers/Button';
import { RichTextWrapper } from 'src/helpers/RichTextWrapper';

export type QuantityCardItemProps =
  Feature.EnterpriseWeb.Enterprise.Forms.Elements.QuantityCardItem & FormFieldProps;

export const getInitialValue = (props: QuantityCardItemProps) => {
  if (props?.fields?.defaultValue) {
    return Number(props?.fields?.defaultValue?.value) || '';
  }
  return '';
};

const QuantityCardItem = (props: QuantityCardItemProps) => {
  const { values, setFieldValue } = useFormikContext<FormikValues>();
  const { themeData } = useTheme(FormFieldsTheme);

  if (!props?.fields || !props.fields?.fieldName?.value) {
    return <></>;
  }

  const increment = () => {
    const currentVal = Number(values[props?.fields?.fieldName?.value]) || 0;
    const maxLimit = props.fields?.maxLength?.value ?? 999;

    if (currentVal < maxLimit) {
      setFieldValue(props?.fields?.fieldName?.value, currentVal + 1, true);
    }
  };

  const decrement = () => {
    const currentVal = Number(values[props?.fields?.fieldName?.value]) || 0;
    const minLimit = props.fields?.minLength?.value ?? 0;

    if (currentVal > minLimit) {
      setFieldValue(props?.fields?.fieldName?.value, currentVal - 1, true);
    }
  };
  const imageProps = {
    fields: {
      primaryImageCaption: {
        value: '',
      },
      primaryImage: props.fields.desktopImage,
      primaryImageMobile: props.fields.mobileImage,
    },
  };

  /* eslint-disable jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */
  return (
    <div className={classNames(themeData.classes.quantityCard.quantityCardItemWrapper)}>
      <div
        className={classNames(
          themeData.classes.quantityCard.quantityCardItem,
          themeData.classes.quantityCard.quantityCardItemDesktop,
          values[props.fields?.fieldName?.value]
            ? themeData.classes.quantityCard.quantityCardItemSelected
            : 'before:border-gray'
        )}
      >
        {imageProps.fields.primaryImage.value?.src && (
          <div className={classNames(themeData.classes.quantityCard.image)}>
            <ImagePrimary
              imageLayout="fill"
              additionalMobileClasses="min-w-[64px] min-h-[64px] max-w-[64px] max-h-[64px]"
              additionalDesktopClasses="min-w-[118px] min-h-[118px] max-w-[118px] max-h-[118px]"
              {...imageProps}
            />
          </div>
        )}
        <div className={classNames(themeData.classes.quantityCard.infoContainer)}>
          {props.fields?.title && (
            <div className={classNames(themeData.classes.quantityCard.title)}>
              <Text field={{ value: props.fields?.title.value }} />
            </div>
          )}
          {props.fields?.description && (
            <div className={classNames(themeData.classes.quantityCard.description)}>
              <RichTextWrapper field={{ value: props.fields?.description.value }} classes="" />
            </div>
          )}
          {props.fields?.label?.value && (
            <label
              className={classNames(themeData.classes.label)}
              htmlFor={props.fields?.fieldName?.value}
            >
              {props.fields.label.value}
              <Tooltip {...props} />
            </label>
          )}
          <Button
            field={props.fields?.cta1Link}
            variant={props.fields?.cta1Style}
            icon={props.fields?.cta1Icon}
            modalId={
              (
                props.fields
                  ?.cta1Modal as unknown as Feature.EnterpriseWeb.Enterprise.Components.Modal.GenericModal.GenericModal
              )?.fields?.modalId?.value
            }
            modalLinkText={props.fields?.cta1ModalLinkText}
            classes={classNames(props.fields?.cta1Style, 'md:whitespace-pre-wrap md:w-lg mt-[4px]')}
            ariaLabel={props.fields?.cta1AriaLabel}
          />
        </div>
      </div>
      <div className={classNames(themeData.classes.quantityCard.numberInput)}>
        <div
          className={classNames(themeData.classes.quantityCard.inputButton, 'ml-l mr-s')}
          onClick={decrement}
        >
          -
        </div>
        <Field
          name={props.fields?.fieldName?.value}
          type="text"
          inputMode="numeric"
          placeholder={props.fields?.placeholderText?.value || ''}
          min={props.fields?.minLength?.value}
          max={props.fields?.maxLength?.value}
          className={classNames(
            'text-center',
            values[props.fields?.fieldName?.value] ? 'border-2 border-primary' : 'border-gray',
            themeData.classes.quantityCard.input
          )}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            //Strip out anything that's not a number
            const val = Number(e.target.value.replace(/\D/g, ''));
            const maxLimit = props.fields?.maxLength?.value ?? 999;
            const minLimit = props.fields?.minLength?.value ?? 0;

            // Clamp keyboard input so they can't type numbers out of set limits
            if (val > maxLimit) {
              setFieldValue(props?.fields?.fieldName?.value, maxLimit);
            } else if (val < minLimit) {
              setFieldValue(props?.fields?.fieldName?.value, minLimit);
            } else {
              setFieldValue(props?.fields?.fieldName?.value, val);
            }
          }}
        />
        <div
          className={classNames(themeData.classes.quantityCard.inputButton, 'mr-l ml-s')}
          onClick={increment}
        >
          +
        </div>
      </div>
    </div>
  );
  /* eslint-enable jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */
};

export default QuantityCardItem;
